/**
 * useApplyAllProgress — manages the Apply All progress modal state.
 *
 * State machine:
 *   idle → submitting → in_progress → completed | failed
 *
 * On `start()`:
 *   1. Sets state to "submitting" immediately so the modal can open.
 *   2. Calls the applyAll HTTP POST (which starts the server-side work).
 *   3. Server emits WS events: apply_all_started → apply_all_progress (×N) →
 *      apply_all_completed | apply_all_failed.
 *   4. Hook listens to those WS messages (filtered by operation_id) and
 *      advances state accordingly.
 *   5. HTTP response arrival is used only as fallback: if `completed` WS event
 *      was already received, HTTP response is ignored; if WS never arrived,
 *      HTTP 200 with status="applied" synthesises the completed state.
 *
 * Apply All can write to multiple household members' surveys in one click.
 * The server emits a started → progress sequence per member group; each event
 * carries `group_label` (the confirmed member's name, or null for the
 * uploader's own group). When `group_label` changes, the hook rebuilds the
 * step labels with the member's name and resets progress so the modal
 * re-runs visibly for each group. One logical click = one operation_id;
 * `apply_all_completed` fires once at the very end.
 *
 * already_applied path: if HTTP returns status="already_applied" we stay idle
 * and let the caller decide (navigate directly, no modal needed).
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { applyAll as applyAllService } from 'src/services/aiFileUploadV2';
import type {
  ApplyAllResultPayload,
  WSApplyAllCompleted,
  WSApplyAllFailed,
  WSApplyAllProgress,
  WSApplyAllStarted,
} from 'src/types/aiFileUploadV2';

// ─── State machine types ──────────────────────────────────────────────────────

export type ApplyAllPhase =
  | 'idle'
  | 'submitting'
  | 'in_progress'
  | 'completed'
  | 'failed';

export interface ApplyAllStep {
  key: string;
  label: string;
  /** true = completed, false/undefined = pending/active */
  completed: boolean;
  /** true = this is the currently active step */
  active: boolean;
}

export interface UseApplyAllProgressState {
  phase: ApplyAllPhase;
  operationId: string | null;
  /** Full name of the household member whose survey group is currently being
   *  written (the confirmed member), or null while the uploader's own group
   *  is being written. Drives the templated step labels. */
  groupLabel: string | null;
  steps: ApplyAllStep[];
  currentStepIndex: number;
  totalSteps: number;
  result: ApplyAllResultPayload | null;
  error: string | null;
}

/**
 * Resolved status from `start()` so callers can route the user immediately
 * after the HTTP response — without waiting for state propagation.
 *
 * - `started`: HTTP returned with operation_id; modal will populate via WS
 *   progress events (or HTTP-fallback `completed` state).
 * - `already_applied`: backend short-circuited (no insights / nothing to write).
 *   No modal interaction needed; caller should navigate the renter forward.
 * - `failed`: HTTP request errored. Caller can rely on `state.phase === 'failed'`.
 * - `noop`: hook was called while not idle or without surveyId; nothing happened.
 */
export type ApplyAllStartResult =
  | { status: 'started' }
  | { status: 'already_applied' }
  | { status: 'failed'; error: string }
  | { status: 'noop' };

export interface UseApplyAllProgressReturn extends UseApplyAllProgressState {
  /** Call when the user clicks "Keep going". Opens modal and fires request. */
  start: () => Promise<ApplyAllStartResult>;
  /** Reset to idle (e.g. after modal is closed or retry). */
  reset: () => void;
  /**
   * Pass these messageHandlers to useWebSocket (or AIFileUploadV2WebSocketProvider)
   * so WS events from the server advance the state.
   */
  wsHandlers: Record<string, (data: unknown) => void>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

// Step keys in the order the backend emits them.
// Used to show all steps in the progress list even before they fire.
export type ApplyAllStepKey = WSApplyAllProgress['step_key'];

/**
 * Build the six Apply-All progress step labels for one member group.
 *
 * - `groupLabel` truthy → labels reference that household member (the confirmed
 *   member whose survey the cross-member document will be written to).
 * - `null` → the uploader's own group, "your" labels.
 *
 * The step keys are identical either way, so progress events advance the same
 * list regardless of which group is running.
 */
export function buildStepLabels(
  groupLabel: string | null
): Array<{ key: ApplyAllStepKey; label: string }> {
  if (groupLabel) {
    return [
      { key: 'starting', label: `Preparing ${groupLabel}'s answers…` },
      { key: 'planning', label: `Getting ${groupLabel}'s info ready…` },
      {
        key: 'writing_gates',
        label: `Organizing ${groupLabel}'s information…`,
      },
      {
        key: 'writing_files',
        label: `Reviewing ${groupLabel}'s documents…`,
      },
      { key: 'writing_answers', label: `Saving ${groupLabel}'s answers…` },
      { key: 'cleanup', label: `Finalizing ${groupLabel}'s file…` },
    ];
  }
  return [
    { key: 'starting', label: 'Preparing your answers…' },
    { key: 'planning', label: 'Getting everything ready…' },
    { key: 'writing_gates', label: 'Organizing your information…' },
    { key: 'writing_files', label: 'Reviewing your documents…' },
    { key: 'writing_answers', label: 'Saving answers…' },
    { key: 'cleanup', label: 'Finalizing…' },
  ];
}

function stepListFromLabels(
  labels: Array<{ key: ApplyAllStepKey; label: string }>
): ApplyAllStep[] {
  return labels.map((s) => ({
    key: s.key,
    label: s.label,
    completed: false,
    active: false,
  }));
}

// ─── Initial state ────────────────────────────────────────────────────────────

const INITIAL_STATE: UseApplyAllProgressState = {
  phase: 'idle',
  operationId: null,
  groupLabel: null,
  steps: stepListFromLabels(buildStepLabels(null)),
  currentStepIndex: -1,
  totalSteps: buildStepLabels(null).length,
  result: null,
  error: null,
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useApplyAllProgress(
  surveyId: number | null,
  targetAnswerGroupId?: number,
  targetQitId?: number | null
): UseApplyAllProgressReturn {
  const [state, setState] = useState<UseApplyAllProgressState>(INITIAL_STATE);

  // Track which operation_id is active so we discard stale WS messages from
  // previous clicks / double-submits.
  const activeOperationIdRef = useRef<string | null>(null);
  // Track whether the WS `completed` event has already been received so we
  // don't double-process when the HTTP response also arrives.
  const wsCompletedRef = useRef(false);

  const reset = useCallback(() => {
    activeOperationIdRef.current = null;
    wsCompletedRef.current = false;
    setState(INITIAL_STATE);
  }, []);

  const handleStarted = useCallback((data: unknown) => {
    const e = data as WSApplyAllStarted;
    // WS events arrive before the HTTP response returns (server emits them
    // synchronously during the request). Bootstrap the operation_id from the
    // first apply_all_started event we receive so subsequent progress events
    // are not filtered out by the operation_id guard.
    if (activeOperationIdRef.current === null) {
      activeOperationIdRef.current = e.operation_id;
    }
    if (e.operation_id !== activeOperationIdRef.current) {
      return;
    }
    setState((prev) => {
      const groupLabel = e.group_label ?? null;
      // Backend emits one started → progress sequence per member group. When
      // the group changes (e.g. moving from the uploader's own survey to a
      // confirmed member's), regenerate the step labels and reset progress so
      // the modal visibly restarts for the new member. Same group → keep the
      // current step list and progress untouched.
      const groupChanged = groupLabel !== prev.groupLabel;
      return {
        ...prev,
        phase: 'in_progress',
        totalSteps: e.total_steps,
        ...(groupChanged
          ? {
              groupLabel,
              steps: stepListFromLabels(buildStepLabels(groupLabel)),
              currentStepIndex: -1,
            }
          : {}),
      };
    });
  }, []);

  const handleProgress = useCallback((data: unknown) => {
    const e = data as WSApplyAllProgress;
    if (e.operation_id !== activeOperationIdRef.current) {
      return;
    }
    setState((prev) => {
      const idx = prev.steps.findIndex((s) => s.key === e.step_key);
      // Guard: ignore progress events for step keys not in our list
      // (e.g. internal steps like 'visibility_sweep'). An unknown key
      // returns idx=-1 which would reset all steps to inactive.
      if (idx === -1) {
        return prev;
      }
      const steps = prev.steps.map((s, i) => ({
        ...s,
        completed: i < idx,
        active: i === idx,
      }));
      return {
        ...prev,
        phase: 'in_progress',
        steps,
        currentStepIndex: idx,
        totalSteps: e.total_steps,
      };
    });
  }, []);

  const handleCompleted = useCallback((data: unknown) => {
    const e = data as WSApplyAllCompleted;
    if (e.operation_id !== activeOperationIdRef.current) {
      return;
    }
    wsCompletedRef.current = true;
    setState((prev) => ({
      ...prev,
      phase: 'completed',
      result: e.result,
      steps: prev.steps.map((s) => ({ ...s, completed: true, active: false })),
      currentStepIndex: prev.steps.length - 1,
    }));
  }, []);

  const handleFailed = useCallback((data: unknown) => {
    const e = data as WSApplyAllFailed;
    if (e.operation_id !== activeOperationIdRef.current) {
      return;
    }
    wsCompletedRef.current = true;
    setState((prev) => ({
      ...prev,
      phase: 'failed',
      error: e.error ?? 'Something went wrong. Please try again.',
    }));
  }, []);

  const start = useCallback(async (): Promise<ApplyAllStartResult> => {
    if (!surveyId || state.phase !== 'idle') {
      return { status: 'noop' };
    }

    wsCompletedRef.current = false;
    setState({
      ...INITIAL_STATE,
      phase: 'submitting',
    });

    try {
      const applyResult = await applyAllService(
        surveyId,
        undefined,
        targetAnswerGroupId,
        targetQitId
      );

      if (applyResult.status === 'already_applied') {
        setState(INITIAL_STATE);
        return { status: 'already_applied' };
      }

      const opId = applyResult.operation_id ?? null;
      // Only set the ref if WS hasn't already bootstrapped it from
      // apply_all_started (which arrives before this HTTP response).
      if (activeOperationIdRef.current === null) {
        activeOperationIdRef.current = opId;
      }

      // Fallback: if WS completed event never arrived (e.g. disconnected),
      // synthesise the completed state from the HTTP response body.
      if (!wsCompletedRef.current) {
        setState((prev) => {
          if (prev.phase === 'completed' || prev.phase === 'failed') {
            return prev;
          }
          return {
            ...prev,
            phase: 'completed',
            result: {
              answers_written: applyResult.answers_written ?? 0,
              answers_skipped: applyResult.answers_skipped ?? 0,
              groups_created: applyResult.groups_created ?? 0,
              files_created: applyResult.files_created ?? 0,
              gates_written: applyResult.gates_written ?? 0,
              files_count: applyResult.files_count ?? 0,
              errors: applyResult.errors ?? [],
            },
            steps: prev.steps.map((s) => ({
              ...s,
              completed: true,
              active: false,
            })),
          };
        });
      }
      return { status: 'started' };
    } catch (err: unknown) {
      const e = err as Error & { response?: { status: number } };
      if (e.name === 'CanceledError' || e.name === 'AbortError') {
        setState(INITIAL_STATE);
        return { status: 'noop' };
      }
      const message = e.message ?? 'Something went wrong. Please try again.';
      setState((prev) => ({
        ...prev,
        phase: 'failed',
        error: message,
      }));
      return { status: 'failed', error: message };
    }
  }, [surveyId, targetAnswerGroupId, targetQitId, state.phase]);

  // wsHandlers is memoised so its object identity is stable across renders.
  // InsightsReview's useEffect depends on it; without useMemo a new object
  // literal would be created every render, causing the effect to re-run and
  // re-register handlers on every state update.
  const wsHandlers = useMemo(
    () => ({
      'renter_hub.apply_all_started': handleStarted,
      'renter_hub.apply_all_progress': handleProgress,
      'renter_hub.apply_all_completed': handleCompleted,
      'renter_hub.apply_all_failed': handleFailed,
    }),
    [handleStarted, handleProgress, handleCompleted, handleFailed]
  );

  return { ...state, start, reset, wsHandlers };
}
